'use client';

import { Play } from 'lucide-react';
import { useState } from 'react';

interface VideoTestimonialProps {
  videoUrl: string;
  title: string;
  author: string;
  thumbnail?: string;
}

export default function VideoTestimonial({ videoUrl, title, author, thumbnail }: VideoTestimonialProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  // Extraire l'ID de la vidéo YouTube
  const getYouTubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const videoId = getYouTubeId(videoUrl);
  const thumbnailUrl = thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  if (!videoId) {
    return (
      <div className="bg-gray-100 rounded-2xl p-8 text-center">
        <p className="text-gray-600">URL de vidéo invalide</p>
      </div>
    );
  }

  return (
    <div className="group bg-white rounded-2xl shadow-sm border-2 border-transparent hover:border-c2e-blue hover:shadow-xl transition-all overflow-hidden">
      <div className="relative aspect-video bg-gray-900">
        {!isPlaying ? (
          <>
            {/* Thumbnail */}
            <img
              src={thumbnailUrl}
              alt={title}
              className="w-full h-full object-cover"
            />
            {/* Overlay avec bouton play */}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
              <button
                onClick={() => setIsPlaying(true)}
                className="bg-c2e-red hover:bg-c2e-red-light text-white rounded-full p-6 transform group-hover:scale-110 transition-all shadow-2xl"
                aria-label="Lire la vidéo"
              >
                <Play className="h-10 w-10 fill-current" />
              </button>
            </div>
          </>
        ) : (
          /* YouTube Iframe */
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        )}
      </div>

      {/* Infos */}
      <div className="p-6">
        <h3 className="font-bold text-gray-900 text-lg mb-2">{title}</h3>
        <p className="text-c2e-blue text-sm font-medium">{author}</p>
      </div>
    </div>
  );
}
